import BN from "bn.js";
import { PublicKey } from "@solana/web3.js";
import { StakingAccount } from "../type";
import { calculateFeeExcludedAmount } from "../math";

export interface IStakingWrapper {
  getStakingAccount(): StakingAccount;
  getOwner(): PublicKey;
  getTokenMint(): PublicKey;
  getTokenVault(): PublicKey;
  getStakedAmount(): BN;
  getUiStakedAmount(): number;
  getPartialUnstakedAmount(): BN;
  getUiPartialUnstakedAmount(): number;
  getPenaltyFeeAmount(): BN;
  getUiPenaltyFeeAmount(): number;
  getTotalLockedAmount(): BN;
  getUiTotalLockedAmount(): number;

  getUnstakeCooldownSeconds(): number;
  getMinStakingAmount(): BN;
  getUiMinStakingAmount(): number;
  getImmediateUnstakePenaltyBps(): number;
  getImmediateUnstakePenaltyPercentage(): number;
  getFeeReceiver(): PublicKey;

  getTotalEscrowCount(): number;

  isUnstakeEnabled(): boolean;
  quoteImmediateUnstake(amount: BN): {
    userReceiveAmount: BN;
    penaltyFeeAmount: BN;
  };
}

export class StakingWrapper implements IStakingWrapper {
  public stakingAccount: StakingAccount;
  public tokenDecimals: number;
  private multiplier: number;

  constructor(stakingAccount: StakingAccount, tokenDecimals: number) {
    this.stakingAccount = stakingAccount;
    this.tokenDecimals = tokenDecimals;
    this.multiplier = 10 ** tokenDecimals;
  }

  getStakingAccount(): StakingAccount {
    return this.stakingAccount;
  }

  getOwner(): PublicKey {
    return this.stakingAccount.owner;
  }

  getTokenMint(): PublicKey {
    return this.stakingAccount.tokenMint;
  }

  getTokenVault(): PublicKey {
    return this.stakingAccount.tokenVault;
  }

  getStakedAmount(): BN {
    return this.stakingAccount.stakedAmount;
  }

  getUiStakedAmount(): number {
    return this.stakingAccount.stakedAmount.toNumber() / this.multiplier;
  }

  getPartialUnstakedAmount(): BN {
    return this.stakingAccount.partialUnstakedAmount;
  }

  getUiPartialUnstakedAmount(): number {
    return (
      this.stakingAccount.partialUnstakedAmount.toNumber() / this.multiplier
    );
  }

  getPenaltyFeeAmount(): BN {
    return this.stakingAccount.penaltyFeeAmount;
  }

  getUiPenaltyFeeAmount(): number {
    return this.stakingAccount.penaltyFeeAmount.toNumber() / this.multiplier;
  }

  getTotalLockedAmount(): BN {
    return this.stakingAccount.stakedAmount
      .add(this.stakingAccount.partialUnstakedAmount)
      .add(this.stakingAccount.penaltyFeeAmount);
  }

  getUiTotalLockedAmount(): number {
    return this.getTotalLockedAmount().toNumber() / this.multiplier;
  }

  getUnstakeCooldownSeconds(): number {
    return this.stakingAccount.config.unstakeCooldownSeconds.toNumber();
  }

  getMinStakingAmount(): BN {
    return this.stakingAccount.config.minStakingAmount;
  }

  getUiMinStakingAmount(): number {
    return (
      this.stakingAccount.config.minStakingAmount.toNumber() / this.multiplier
    );
  }

  getImmediateUnstakePenaltyBps(): number {
    return this.stakingAccount.config.immediateUnstakePenaltyBps;
  }

  getImmediateUnstakePenaltyPercentage(): number {
    return this.stakingAccount.config.immediateUnstakePenaltyBps / 100;
  }

  getFeeReceiver(): PublicKey {
    return this.stakingAccount.config.feeReceiver;
  }

  getTotalEscrowCount(): number {
    return this.stakingAccount.metrics.totalEscrowCount.toNumber();
  }

  isUnstakeEnabled(): boolean {
    return this.stakingAccount.config.immediateUnstakePenaltyBps > 0;
  }

  quoteImmediateUnstake(amount: BN): {
    userReceiveAmount: BN;
    penaltyFeeAmount: BN;
  } {
    const { feeExcludedAmount, feeAmount } = calculateFeeExcludedAmount(
      amount,
      this.stakingAccount.config.immediateUnstakePenaltyBps,
    );

    return {
      userReceiveAmount: feeExcludedAmount,
      penaltyFeeAmount: feeAmount,
    };
  }
}
